<template>
  <div>
    <b-row>
      <b-col md="7">
        <zone-map
          :polygon="zone.polygon"
          @zones-set="zone.polygon=$event"
        />
      </b-col>
      <b-col
        class="pt-5"
        md="5"
      >
        <validation-observer ref="zoneRefObserver">
          <b-form
            autocomplete="off"
            @submit.prevent="putZone"
          >
            <validation-provider
              name="Name"
              rules="required"
            >
              <b-form-group
                slot-scope="{ valid, errors }"
                label="Name"
                label-for="name"
              >
                <b-form-input
                  id="name"
                  v-model="zone.name"
                  :state="errors[0] ? false : (valid ? true : null)"
                  placeholder="Name"
                  class="mb-1"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              name="Country"
              rules="required"
            >
              <b-form-group
                slot-scope="{ valid, errors }"
                label="Country"
                label-for="country"
              >
                <b-form-input
                  id="country"
                  v-model="zone.country"
                  disabled
                  :state="errors[0] ? false : (valid ? true : null)"
                  placeholder="Country"
                  class="mb-1"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              name="City"
              rules="required"
            >
              <b-form-group
                slot-scope="{ valid, errors }"
                label="City"
                label-for="city"
              >
                <b-form-input
                  id="city"
                  v-model="zone.city"
                  :state="errors[0] ? false : (valid ? true : null)"
                  placeholder="City"
                  class="mb-1"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              name="Area"
              rules="required"
            >
              <b-form-group
                slot-scope="{ valid, errors }"
                label="Area"
                label-for="area"
              >
                <b-form-input
                  id="area"
                  v-model="zone.area"
                  :state="errors[0] ? false : (valid ? true : null)"
                  placeholder="Area"
                  class="mb-1"
                  disabled
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              name="Status"
              rules="required"
            >
              <b-form-group
                slot-scope="{ valid, errors }"
                label="Status"
                label-for="status"
              >
                <b-form-radio-group
                  id="status"
                  v-model="zone.status"
                  :state="errors[0] ? false : (valid ? true : null)"
                  :options="statusOptions"
                  @change="showConfirmMessage"
                />

                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-button
              type="submit"
              variant="primary"
              :disabled="putZoneBtnLoading"
            >
              <b-spinner
                v-if="putZoneBtnLoading"
                small
              />
              <span v-else>Submit</span>
            </b-button>
            <p
              v-if="!zoneSelected"
              class="add-zone__map-error"
            >
              Please select a zone
            </p>
          </b-form>
        </validation-observer>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  reactive, ref, computed, onUnmounted,
} from '@vue/composition-api'
import {
  BButton, BRow, BCol, BFormInput, BForm, BFormGroup, BFormInvalidFeedback, BFormRadioGroup, BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useToast } from 'vue-toastification/composition'
import ZoneMap from './ZoneMap.vue'
import store from '@/store'
import zoneStoreModule from '@/views/zones/zoneStoreModule'

export default {
  components: {
    BSpinner,
    BFormRadioGroup,
    BFormGroup,
    BForm,
    BFormInvalidFeedback,
    BFormInput,
    BButton,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    ZoneMap,
  },
  props: {
    isNew: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const SERVICE_APP_STORE_MODULE_NAME = 'zones'
    // Register module
    if (!store.hasModule(SERVICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(SERVICE_APP_STORE_MODULE_NAME, zoneStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SERVICE_APP_STORE_MODULE_NAME)) store.unregisterModule(SERVICE_APP_STORE_MODULE_NAME)
    })
    const toast = useToast()
    const statusOptions = [
      { text: 'Active', value: 'ACTIVE' },
      { text: 'Inactive', value: 'INACTIVE' },
    ]
    const zoneSelected = ref(true)
    const zoneRefObserver = ref(null)
    let zone = reactive({
      polygon: [],
      name: '',
      country: 'Jordan',
      city: '',
      area: 'None',
      status: 'INACTIVE',
    })
    if (!props.isNew) {
      const zoneInfo = computed(() => store.getters['zones/selectedZone'])
      zoneInfo.value.polygon.forEach(el => {
        // eslint-disable-next-line no-param-reassign
        el.lat = Number(el.lat)
        // eslint-disable-next-line no-param-reassign
        el.lng = Number(el.lng)
      })
      zone = zoneInfo.value
    }
    const putZoneBtnLoading = ref(false)
    async function putZone() {
      zoneSelected.value = true
      const isValid = await zoneRefObserver.value.validate()
      if (!isValid) {
        return
      }
      if (zone.polygon.length === 0) {
        zoneSelected.value = false
        return
      }
      putZoneBtnLoading.value = true
      store.dispatch('zones/putZone', {
        ...zone,
        polygon: [...zone.polygon, zone.polygon[0]],
      }).then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Done',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        putZoneBtnLoading.value = false
      })
    }
    return {
      statusOptions,
      zone,
      zoneSelected,
      putZone,
      required,
      zoneRefObserver,
      putZoneBtnLoading,
    }
  },
  methods: {
    showConfirmMessage(value) {
      this.$swal({
        title: 'Are you sure?',
        text: 'You want to approve change status',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Approve',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.zone.status = value
        } else if (!result.value) {
          if (this.zone.status === 'ACTIVE') {
            this.zone.status = 'INACTIVE'
          } else {
            this.zone.status = 'ACTIVE'
          }
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.add-zone__map-error {
  color:red;
  font-weight:bold;
}
</style>
